import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Mail } from "lucide-react";
import api from "@/lib/api";
import { AppLogo } from "@/components/Shared";

/**
 * Rende il testo dell'informativa: paragrafi separati da riga vuota; le righe
 * che iniziano con "# " diventano titoli di sezione. Solo nodi di testo,
 * nessun HTML iniettato.
 */
export function RenderInformativa({ testo }) {
  const blocchi = (testo || "").split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
      {blocchi.map((b, i) =>
        b.startsWith("# ") ? (
          <h2 key={i} className="font-display font-extrabold text-slate-900 !mt-5 first:!mt-0">{b.slice(2)}</h2>
        ) : (
          <p key={i} className="whitespace-pre-wrap">{b}</p>
        )
      )}
    </div>
  );
}

export default function Privacy() {
  const [dati, setDati] = useState(null);

  useEffect(() => {
    api.get("/public/privacy").then((r) => setDati(r.data)).catch(() => setDati({ testo: "", personalizzato: false }));
  }, []);

  return (
    <div className="min-h-dvh bg-[#FFFDF9] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col items-center mb-6">
          <AppLogo size={56} className="mb-3 drop-shadow-lg" />
          <h1 className="font-display text-2xl font-extrabold text-slate-900 text-center flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-emerald-500" /> Informativa Privacy
          </h1>
          <p className="text-sm font-semibold text-slate-500 text-center">
            Trattamento dei dati personali · Oratorio Estivo SS. Medici
          </p>
        </div>

        <div className="card-app p-6 md:p-8">
          {!dati ? (
            <p className="text-slate-400 font-semibold text-center py-6">Caricamento...</p>
          ) : (
            <>
              {!dati.personalizzato && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-2xl p-3 font-semibold mb-5">
                  Documento di riferimento non ancora personalizzato: i dati identificativi vanno compilati dalla parrocchia.
                </p>
              )}
              <RenderInformativa testo={dati.testo} />
              {dati.aggiornato_il && (
                <p className="text-xs text-slate-400 font-semibold pt-5 mt-4 border-t border-slate-100">
                  Ultimo aggiornamento: {new Date(dati.aggiornato_il).toLocaleDateString("it-IT")}
                </p>
              )}
            </>
          )}
        </div>

        {/* --- COME ESERCITARE I PROPRI DIRITTI ---
            Un'informativa che elenca i diritti senza dire a chi scrivere li
            rende teorici. Questo blocco è la parte operativa: cosa si può
            chiedere, a chi, in quanto tempo, e a chi rivolgersi se la risposta
            non arriva. */}
        {dati?.contatti_diritti && (
          <div className="card-app p-6 md:p-8 mt-4" data-testid="privacy-diritti">
            <h2 className="font-display font-extrabold text-slate-900 flex items-center gap-2 mb-3">
              <Mail className="h-5 w-5 text-amber-500" /> Come esercitare i tuoi diritti
            </h2>
            <p className="text-sm text-slate-700 leading-relaxed">
              Puoi chiedere in qualsiasi momento di <b>vedere</b> i dati che conserviamo su di te
              o su tuo figlio, di <b>correggerli</b> se sono sbagliati, di <b>cancellarli</b>, di
              <b> limitarne l'uso</b>, di <b>opporti</b> al trattamento e di <b>riceverli</b> in un
              formato leggibile da un altro programma. Se hai dato un consenso, puoi <b>revocarlo</b>:
              revocarlo non rende illecito ciò che è stato fatto prima.
            </p>

            {dati.contatti_diritti.completi ? (
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 mt-4"
                data-testid="privacy-contatti">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">
                  Scrivi a
                </p>
                <p className="text-base font-bold text-slate-900 break-all">
                  <a href={`mailto:${dati.contatti_diritti.email}`} className="underline">
                    {dati.contatti_diritti.email}
                  </a>
                </p>
                {dati.contatti_diritti.referente && (
                  <p className="text-sm text-slate-600 font-semibold mt-1">
                    Referente: {dati.contatti_diritti.referente}
                  </p>
                )}
                {dati.contatti_diritti.denominazione && (
                  <p className="text-xs text-slate-500 font-semibold mt-2">
                    {dati.contatti_diritti.denominazione}
                    {dati.contatti_diritti.sede ? ` · ${dati.contatti_diritti.sede}` : ""}
                    {dati.contatti_diritti.telefono ? ` · ${dati.contatti_diritti.telefono}` : ""}
                  </p>
                )}
                <p className="text-sm text-slate-700 mt-3">
                  Rispondiamo entro <b>{dati.contatti_diritti.giorni_termine} giorni</b>.
                  Per verificare che la richiesta arrivi davvero da te, potremmo chiederti
                  di identificarti.
                </p>
              </div>
            ) : (
              /* Nessun indirizzo inventato: se manca, si dice che manca. Una
                 richiesta spedita a una casella sbagliata non arriva a nessuno,
                 ma il termine di legge decorre lo stesso. */
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mt-4"
                data-testid="privacy-contatti-mancanti">
                <p className="text-sm font-bold text-amber-900 mb-1">
                  Recapito non ancora pubblicato
                </p>
                <p className="text-sm text-slate-700">
                  L'indirizzo a cui inviare le richieste non è ancora stato indicato dalla
                  parrocchia. Nel frattempo rivolgiti di persona alla segreteria parrocchiale
                  o a un responsabile dell'oratorio: la tua richiesta è valida comunque.
                </p>
              </div>
            )}

            <p className="text-xs text-slate-500 leading-relaxed mt-4 pt-4 border-t border-slate-100">
              Se ritieni che i tuoi dati siano trattati in modo non corretto puoi presentare
              reclamo al <b>Garante per la protezione dei dati personali</b>{" "}
              (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer"
                className="underline">garanteprivacy.it</a>), oppure rivolgerti all'autorità
              giudiziaria. Il reclamo al Garante è gratuito.
            </p>
          </div>
        )}

        <div className="text-center mt-6">
          <Link to="/login" className="btn-ghost" data-testid="privacy-torna-login">
            <ArrowLeft className="h-4 w-4" /> Torna al login
          </Link>
        </div>
      </div>
    </div>
  );
}
